/**
 * Ryan O'Dowd
 * 2024-02-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  PRACTICE_ROUTE,
  READ_ROUTE,
  addToReadingHistory,
  updateNotificationPreferences,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import AddToGoalDialog from '@components/AddToGoalDialog';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import {
  Checkbox, // @TODO: replace with common abstraction
} from 'react-native-paper';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import IconButton from '@components/common/IconButton';
import Menu from '@components/common/Menu';
import PassageDisplay from '@components/PassageDisplay';
import ShareButton from '@components/ShareButton';
import Text from '@components/common/Text';
import {
  TimePickerModal,
} from 'react-native-paper-dates';
import WidgetBase from '@components/WidgetBase';
/* @TODO:
import {
  channelIds,
} from '@utils/globals';
*/
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const PassageOfTheDay = (props: PassageOfTheDayProps) => {
  const navigation = hooks.useNavigation();
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const notificationPreferences = hooks.useTypedSelector((state) => state.notificationPreferences);

  const [_isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [_isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [_isDifficultyMenuOpen, setIsDifficultyMenuOpen] = useState(false);
  const [_passageText, setPassageText] = useState('');

  const [_isTimePickerVisible, setIsTimePickerVisible] = useState(false);

  const passageOfTheDayReminderTime = hooks.useTypedSelector((state) => state.notificationPreferences.passage_of_the_day_time);

  const initialTimeDateObject = new Date();
  const [hours, minutes] = passageOfTheDayReminderTime?.split(':') || ['08', '00']; // @TODO: prefer not to have a default value here
  initialTimeDateObject.setHours(hours);
  initialTimeDateObject.setMinutes(minutes);

  const preferredTranslation = hooks.usePreferredTranslation();
  const passageOfTheDay = hooks.useTypedSelector((state: any) => state.passageOfTheDay.passage || {}, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: shouldn't need the || {} but be careful of missing from back end or initial state
  const isPassageOfTheDayComplete = hooks.useTypedSelector((state: any) => !!state.passageOfTheDay.isComplete);

  const passage = new CPassage(
    -1, // @TODO: was using passageOfTheDay.id before, but that means that the back end is saving the lessons with the passage_id of passageOfTheDay.id instead of the passage.id; this is hacky to use -1, but it's required right now because of the _saveLesson function in Practice/index.native.js
    preferredTranslation.id || 3, // @TODO: race condition here on initial login on web (white screen issue)
    passageOfTheDay.book_id,
    passageOfTheDay.chapter_number,
    passageOfTheDay.start_verse_number,
    passageOfTheDay.end_verse_number,
  );

  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
    >
      <WidgetBase.Header
        title={i18n('passageOfTheDayWidgetTitle')}
        rightIconName='cog'
        onPressRightIcon={() => setIsSettingsDialogOpen(true)}
      />
      <WidgetBase.Content
        style={styles.container}
        noBackground={true}
      >
        {Object.keys(passageOfTheDay).length > 0
          ? (
            <View style={styles.verseWrapper}>
              <Text style={styles.passageOfTheDayTitle}>{passage.prettyPassageName()}, {preferredTranslation.abbreviation || ''}</Text>{/* @TODO: includeAbbreviation should probably be kwarg to prettyPassageName to avoid using the preferredTranslation here */}
              <PassageDisplay
                key={passage.id}
                mainListContainerStyle={styles.passageDisplayOverrides}
                verseTextStyles={styles.passageText}// @TODO: not taking effect...want this to be a pinch smaller
                passages={[passage]}
                hideLicensing={true}
                showFormatting={false}
                showFootnotes={false}
                showHeadings={false}
                hideCompare={true}
                hideAddToGoal={true}
                isPressDisabled={true}
                textColorOverride={styles.colors.white}
                showWrinkleBadges={false}
                savePassageText={(text) => setPassageText(text)}
              />
            </View>
          ) : (
            <EmptyScreen.Wrapper>
              <EmptyScreen.Text>
                {i18n('noPassageOfTheDay')}
              </EmptyScreen.Text>
            </EmptyScreen.Wrapper>
          )
        }
      </WidgetBase.Content>
      <View style={styles.passageOfTheDayActions}>{/* @TODO: add text labels? */}
        <IconButton
          style={styles.passageOfTheDayAction}
          icon='book'
          iconColor={styles.colors.white}
          onPress={() => {
            const chapterId = CPassage.getChapterByBookIdAndNumber(passageOfTheDay.book_id, passageOfTheDay.chapter_number).id;
            dispatch(addToReadingHistory(chapterId));
            navigation.navigate(READ_ROUTE, {
              chapterId,
              contextPassage: passageOfTheDay,
            });
          }}
        />
        <IconButton
          style={styles.passageOfTheDayAction}
          icon='playlist-plus'
          iconColor={styles.colors.white}
          onPress={() => setIsGoalDialogOpen(true)}
        />
        <Menu
          anchor={
            <View>
              <IconButton
                style={styles.passageOfTheDayAction}
                icon='school'
                iconColor={isPassageOfTheDayComplete ? `${styles.colors.white}88` : styles.colors.white}
                onPress={() => setIsDifficultyMenuOpen(true)}
              />{/* @TODO: should this have a badge prop? */}
              {isPassageOfTheDayComplete
                ? (
                  <Badge
                    size={12}
                    top={4}
                    right={4}
                    color={styles.colors.accent}
                  >
                    ✓
                  </Badge>
                ) : (
                  <Badge
                    size={12}
                    top={4}
                    right={4}
                  />
                )
              }
            </View>
          }
          visible={_isDifficultyMenuOpen}
          onDismiss={() => setIsDifficultyMenuOpen(false)}
        >
          {['easy', 'medium', 'hard'].map((difficulty) => ( // @TODO: don't hardcode this list
            <Menu.Item
              key={difficulty}
              title={utilities.capitalizeFirstLetter(difficulty)}
              onPress={() => {
                setIsDifficultyMenuOpen(false);
                navigation.navigate(PRACTICE_ROUTE, {
                  passages: [passage],
                  passageOfTheDayId: passageOfTheDay.id,
                  difficultyOverride: difficulty,
                  noGoal: true,
                });
              }}
            />
          ))}
        </Menu>
        <ShareButton
          style={styles.passageOfTheDayAction}
          passage={passage}
          passageText={_passageText}
          locationForAnalytics='mobile_home_passage_of_the_day_widget'
        />
      </View>
      {_isGoalDialogOpen && <AddToGoalDialog onClose={() => setIsGoalDialogOpen(false)} passage={passage} />}
      <Dialog
        visible={_isSettingsDialogOpen}
        onDismiss={() => setIsSettingsDialogOpen(false)}
      >
        <Dialog.Title>{i18n('passageOfTheDaySettings')}</Dialog.Title>
        <Dialog.Content>
          <View style={styles.sectionWrapperTODO}>
            {/* @TODO: replace this dialog content with the same component that's used in settings -> notifications */}
            <TouchableOpacity
              style={styles.checkAndTitle}
              onPress={() => {
                const currOn = notificationPreferences.passage_of_the_day_channels.push;
                dispatch(updateNotificationPreferences({passage_of_the_day_on_push: +!currOn}));
                // @TODO: should be a back-end call: utilities.schedulePassageOfTheDayNotifications(!currOn);
              }}
            >
              <Checkbox.Android status={notificationPreferences.passage_of_the_day_on ? 'checked' : 'unchecked'} />
              <Text style={styles.sectionTitleTODO}>{i18n('passageOfTheDayNotifications')}</Text>
            </TouchableOpacity>
            <Text style={styles.descriptionText}>{i18n('passageOfTheDayNotificationsDescription')}</Text>
            <View style={styles.actionsWrapper}>
              <Text style={styles.timeText}>{i18n('remindersWillBeSentAt')} </Text>
              <TouchableOpacity
                style={styles.timeDisplay}
                onPress={() => setIsTimePickerVisible(true)}
              >
                <Text style={[styles.timeText, styles.time]}>{dateFnsFormat(initialTimeDateObject, "h:mmaaaaa'm'", {locale: i18n.dateFnsLocale})}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsSettingsDialogOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <TimePickerModal
        visible={_isTimePickerVisible}
        hours={hours}
        minutes={minutes}
        onConfirm={({hours, minutes}) => {
          dispatch(updateNotificationPreferences({passage_of_the_day_time: `${hours}:${minutes}`}));
          setIsTimePickerVisible(false);
        }}
        onDismiss={() => {
          setIsTimePickerVisible(false);
        }}
      />
    </WidgetBase.Container>
  );
};

class PassageOfTheDayProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;
};

export default PassageOfTheDay;
